"use client";

import { useTranslations } from "next-intl";
import { useEffect, useRef, useState, useTransition } from "react";

import { SearchInput } from "@/components/ui/search-input";
import { usePathname, useRouter } from "@/i18n/navigation";
import { trackSearch } from "@/lib/analytics";

interface DiscoverSearchInputProps {
  initialQuery: string;
  className?: string;
  logSearches?: boolean;
  searchSource?: "recipes" | "discover";
}

const DEBOUNCE_MS = 250;
const LOG_SETTLE_MS = 3000;

function logSearch(query: string) {
  fetch("/api/tags/log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tagName: query, eventType: "search_query" }),
  }).catch(() => {});
}

export function DiscoverSearchInput({
  initialQuery,
  className,
  logSearches,
  searchSource = "discover",
}: DiscoverSearchInputProps) {
  const t = useTranslations("discover");
  const router = useRouter();
  const pathname = usePathname();
  const [value, setValue] = useState(initialQuery);
  const [isPending, startTransition] = useTransition();
  const lastPushed = useRef(initialQuery);
  const lastLogged = useRef(initialQuery);
  const logTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (value === lastPushed.current) return;
    const handle = setTimeout(() => {
      const trimmed = value.trim();
      const params = new URLSearchParams();
      if (trimmed) params.set("q", trimmed);
      const target = params.toString() ? `${pathname}?${params}` : pathname;
      lastPushed.current = value;
      startTransition(() => {
        router.replace(target, { scroll: false });
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value, pathname, router]);

  useEffect(() => {
    if (!logSearches) return;
    clearTimeout(logTimerRef.current);
    const trimmed = value.trim();
    if (!trimmed || trimmed === lastLogged.current) return;
    logTimerRef.current = setTimeout(() => {
      lastLogged.current = trimmed;
      logSearch(trimmed);
      trackSearch({ query: trimmed, source: searchSource });
    }, LOG_SETTLE_MS);
    return () => clearTimeout(logTimerRef.current);
  }, [value, logSearches, searchSource]);

  useEffect(() => {
    if (!logSearches) return;
    return () => {
      clearTimeout(logTimerRef.current);
      const trimmed = lastPushed.current?.trim();
      if (trimmed && trimmed !== lastLogged.current) {
        lastLogged.current = trimmed;
        logSearch(trimmed);
        trackSearch({ query: trimmed, source: searchSource });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SearchInput
      value={value}
      onChange={setValue}
      placeholder={t("searchPlaceholder")}
      clearLabel={t("clearSearch")}
      loading={isPending}
      className={className}
    />
  );
}
